/**
 * QuickBooks IIF lists import — the structure half of a QB rebuild: ACCNT →
 * gl_codes, VEND → vendors (tax IDs client-encrypted, Secure Fields pattern),
 * CUST → accounts + people + owner links (with closed-duplicate account-number
 * disambiguation).
 *
 * Extracted from the Step-1 modal so the UNIFIED bundle importer can apply
 * the IIF before compiling — one implementation, two callers
 * (components/accounting/import-quickbooks-modal.tsx and the bundle modal's
 * all-files-at-once flow).
 */

import { accountingService } from '@/services/accounting-service';
import { GlCodeAccountType } from '@/types/models';
import { type IIFParse } from '@/utils/iif-parser';
import { buildCustomerImportRows } from '@/utils/qb-customer-rows';

export type IifListsProgressPhase = 'glCodes' | 'vendors' | 'customers';

export interface IifListsSummary {
	glCodes: { inserted: number; updated: number; skipped: number };
	vendors: { inserted: number; updated: number };
	accounts: number;
	/** Existing accounts whose data was overwritten (re-import formatting fixes). */
	accountsUpdated: number;
	people: number;
	skipped: number;
	errors: string[];
}

// Customers per chunked POST — see the server-side /customers/bulk handler;
// 50 keeps each POST well under the client timeout.
const CUSTOMER_CHUNK_SIZE = 50;

/** Count of vendors carrying a plaintext TAXID — those REQUIRE `encrypt`. */
export function countIifTaxIds(parsed: IIFParse): number {
	return parsed.vendors.filter((v) => ((v.raw.TAXID || '').trim()).length > 0).length;
}

// Encrypt a plaintext tax ID and pair it with a `***-**-####` mask. Spreads
// into the bulk-upsert payload; empty input yields `{}`.
const buildSecureTaxIdFields = (
	plaintext: string,
	encrypt: (s: string) => string,
): { taxId?: string; taxIdMask?: string } => {
	if (!plaintext) return {};
	const last4 = plaintext.replace(/\D/g, '').slice(-4);
	return {
		taxId: encrypt(plaintext),
		taxIdMask: last4 ? `***-**-${last4}` : undefined,
	};
};

// Moved to utils/qb-customer-rows.ts (shared with preflight and the import spec);
// re-exported so existing imports keep working.
export { disambiguateAccountNumbers } from '@/utils/qb-customer-rows';

export interface ImportIifListsArgs {
	associationId: string;
	parsed: IIFParse;
	/** Required when the file carries vendor TAXIDs — plaintext EINs must
	 *  NEVER reach the server (Secure Fields mandate). */
	encrypt: ((s: string) => string) | null;
	/** Tag every created row so a batch revert sweeps the structure too. */
	importBatchId?: string;
	onProgress?: (phase: IifListsProgressPhase, now: number, total: number) => void;
}

/**
 * Apply the IIF lists to an association. Idempotent: GL codes upsert by
 * (association, code), vendors by name, customers by externalId/account
 * number. Throws if tax IDs are present without an encrypt function.
 */
export async function importIifLists(args: ImportIifListsArgs): Promise<IifListsSummary> {
	const { associationId, parsed, encrypt, importBatchId, onProgress } = args;
	const errors: string[] = [];
	let skipped = 0;

	if (countIifTaxIds(parsed) > 0 && !encrypt) {
		throw new Error('This IIF carries vendor tax IDs — unlock sensitive data first so they can be encrypted before upload.');
	}

	// ── ACCNT → gl_codes (association-scoped chart) ────────────────────────
	onProgress?.('glCodes', 0, parsed.accounts.length);
	let glCodes = { inserted: 0, updated: 0, skipped: 0 };
	const glRows = parsed.accounts.map((a) => {
		const acctNum = (a.raw.ACCNUM || '').trim();
		const fullPath = (a.raw.NAME || '').trim();
		const leafName = fullPath.includes(':') ? fullPath.slice(fullPath.lastIndexOf(':') + 1) : fullPath;
		const rawType = (a.raw.ACCNTTYPE || 'OTHER').trim().toUpperCase();
		const accountType = (Object.values(GlCodeAccountType) as string[]).includes(rawType)
			? (rawType as GlCodeAccountType)
			: GlCodeAccountType.Other;
		return {
			// Blank ACCNUM (summary parent rows) falls back to the full IIF name
			// so the unique-by-code constraint still holds.
			code: acctNum || fullPath,
			name: leafName,
			fullPath,
			accountType,
			description: (a.raw.DESC || '').trim() || undefined,
			active: (a.raw.HIDDEN || '').toUpperCase() !== 'Y',
			externalId: (a.raw.REFNUM || '').trim() || undefined,
		};
	}).filter((r) => r.code && r.name);
	if (glRows.length > 0) {
		try {
			const res = await accountingService.bulkUpsertGlCodes(glRows, { importBatchId });
			glCodes = { inserted: res.inserted, updated: res.updated, skipped: res.skipped ?? 0 };
		} catch (e) {
			errors.push(`GL codes bulk import: ${(e as Error).message}`);
		}
	}
	onProgress?.('glCodes', parsed.accounts.length, parsed.accounts.length);

	// ── VEND → vendors (tax IDs encrypted client-side) ─────────────────────
	onProgress?.('vendors', 0, parsed.vendors.length);
	let vendors = { inserted: 0, updated: 0 };
	if (parsed.vendors.length > 0) {
		const rows = parsed.vendors.map((v) => {
			const name = (v.raw.NAME || '').trim();
			const addrLines = [v.raw.ADDR1, v.raw.ADDR2, v.raw.ADDR3, v.raw.ADDR4, v.raw.ADDR5]
				.map((s) => (s || '').trim().replace(/^"|"$/g, ''))
				.filter(Boolean);
			const printedName = ((v.raw.PRINTAS || '').trim() || name).replace(/^"|"$/g, '');
			const linesForAddr = addrLines[0] && addrLines[0] === printedName
				? addrLines.slice(1) : addrLines;
			let city: string | undefined;
			let state: string | undefined;
			let zip: string | undefined;
			let cityStateZipMatch: RegExpMatchArray | null = null;
			for (let i = linesForAddr.length - 1; i >= 0; i--) {
				const m = linesForAddr[i].match(/^(.+?),\s*([A-Z]{2})\s+([\d-]{5,10})$/);
				if (m) { cityStateZipMatch = m; linesForAddr.splice(i, 1); break; }
			}
			if (cityStateZipMatch) {
				[, city, state, zip] = cityStateZipMatch;
			}
			return {
				name,
				externalId: (v.raw.REFNUM || '').trim() || undefined,
				printAs: (v.raw.PRINTAS || '').trim() || undefined,
				companyName: (v.raw.COMPANYNAME || '').trim() || undefined,
				contactFirstName: (v.raw.FIRSTNAME || '').trim() || undefined,
				contactMiddleInit: (v.raw.MIDINIT || '').trim() || undefined,
				contactLastName: (v.raw.LASTNAME || '').trim() || undefined,
				email: (v.raw.EMAIL || '').trim() || undefined,
				phone: (v.raw.PHONE1 || '').trim() || undefined,
				phoneAlt: (v.raw.PHONE2 || '').trim() || undefined,
				fax: (v.raw.FAXNUM || '').trim() || undefined,
				addressLine1: linesForAddr[0] || undefined,
				addressLine2: linesForAddr[1] || undefined,
				addressLine3: linesForAddr[2] || undefined,
				city, state, zip,
				...(encrypt ? buildSecureTaxIdFields((v.raw.TAXID || '').trim(), encrypt) : {}),
				is1099: (v.raw['1099'] || '').toUpperCase() === 'Y',
				vendorType: (v.raw.VTYPE || '').trim() || undefined,
				terms: (v.raw.TERMS || '').trim() || undefined,
				accountNumber: (v.raw.ACCNUM || '').trim() || undefined,
				notes: (v.raw.NOTEPAD || v.raw.NOTE || '').trim() || undefined,
				active: (v.raw.HIDDEN || '').toUpperCase() !== 'Y',
			};
		}).filter((r) => r.name);
		if (rows.length > 0) {
			try {
				const result = await accountingService.bulkUpsertVendors(rows, importBatchId);
				vendors = result;
				for (const rowError of result.errors ?? []) {
					errors.push(`Vendor "${rowError.name}": ${rowError.error}`);
				}
			} catch (e) {
				errors.push(`Vendors bulk import: ${(e as Error).message}`);
			}
		}
	}
	onProgress?.('vendors', parsed.vendors.length, parsed.vendors.length);

	// ── CUST → accounts + people + owner links ─────────────────────────────
	const built = buildCustomerImportRows(parsed.customers);
	const customerRows: Parameters<typeof accountingService.bulkImportCustomers>[0]['rows'] = built.rows;
	for (const name of built.skippedNames) {
		errors.push(`Skipped "${name}": no unit/account-number prefix detected`);
		skipped++;
	}
	// Not errors — numbering the office should know about (also shown by the pre-import check).
	for (const note of built.notes) errors.push(note.message);

	let accounts = 0;
	let accountsUpdated = 0;
	let people = 0;
	onProgress?.('customers', 0, customerRows.length);
	for (let i = 0; i < customerRows.length; i += CUSTOMER_CHUNK_SIZE) {
		const chunk = customerRows.slice(i, i + CUSTOMER_CHUNK_SIZE);
		try {
			const res = await accountingService.bulkImportCustomers({
				associationId, importBatchId, rows: chunk,
			});
			accounts += res.accountsCreated;
			accountsUpdated += res.accountsUpdated;
			people += res.peopleCreated;
			skipped += res.skipped;
			// Not an error, but a renumbered account is something the person importing
			// must be able to see — it changes which number the closed account prints under.
			for (const r of res.accountsRenumbered ?? []) {
				errors.push(`Renumbered account ${r.from} → ${r.to} (office numbering: the live owner holds the bare number)`);
			}
			for (const c of res.accountNumberConflicts ?? []) errors.push(c.message);
			for (const r of res.accountsNumbered ?? []) {
				errors.push(`Numbered account "${r.externalName ?? '?'}" as ${r.number} (it had no account number — created by an earlier journal import)`);
			}
			for (const e of res.errors) {
				errors.push(`Customer "${e.accountNumber ?? e.externalName ?? '?'}": ${e.message}`);
			}
		} catch (e) {
			errors.push(
				`Customer chunk ${Math.floor(i / CUSTOMER_CHUNK_SIZE) + 1} failed (rows ${i + 1}–${i + chunk.length}): ${(e as Error).message}. Some rows may have saved — refresh the Batches list.`,
			);
		}
		onProgress?.('customers', Math.min(customerRows.length, i + chunk.length), customerRows.length);
	}

	return { glCodes, vendors, accounts, accountsUpdated, people, skipped, errors };
}
